import React, { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import ApiService from '../../../../services/ApiService'
import {
  CloseButton,
  ErrorText,
  FieldInput,
  FieldLabel,
  FieldSelect,
  FormRow,
  Overlay,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PrimaryButton,
  SecondaryButton,
} from '../ModalStyle'

interface Company {
  id: number
  companyName: string
}

interface CreateOrderModalProps {
  onClose: () => void
  onCreated: () => void
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({ onClose, onCreated }) => {
  const [companies, setCompanies] = useState<Company[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [title, setTitle] = useState('')
  const [type, setType] = useState<'COMPRA_PONTUAL' | 'INDUSTRIALIZACAO'>('COMPRA_PONTUAL')
  const [status, setStatus] = useState<'EM_COTACAO' | 'APROVADO' | 'PEDIDO_EMITIDO'>('EM_COTACAO')
  const [supplierCompanyId, setSupplierCompanyId] = useState('')
  const [deadline, setDeadline] = useState('')
  const [description, setDescription] = useState('')
  const [observations, setObservations] = useState('')
  const [itemDescription, setItemDescription] = useState('')
  const [itemQuantity, setItemQuantity] = useState('')
  const [itemUnitOfMeasurement, setItemUnitOfMeasurement] = useState('')
  const [totalValue, setTotalValue] = useState('')

  useEffect(() => {
    ApiService.companies
      .findAll({ take: 200 })
      .then((res: any) => setCompanies(res.data?.data ?? res.data ?? []))
      .catch(() => setCompanies([]))
  }, [])

  const isValid = title.trim() && supplierCompanyId && deadline && totalValue !== ''

  const handleSubmit = async () => {
    if (!isValid || saving) return
    setSaving(true)
    setError(null)
    try {
      await ApiService.purchaseOrder.create({
        title: title.trim(),
        type,
        status,
        supplierCompanyId: Number(supplierCompanyId),
        deadline,
        description: description.trim() || undefined,
        observations: observations.trim() || undefined,
        totalValue: Number(totalValue),
        itemDescription: itemDescription.trim() || undefined,
        itemQuantity: itemQuantity !== '' ? Number(itemQuantity) : undefined,
        itemUnitOfMeasurement: itemUnitOfMeasurement.trim() || undefined,
      })
      onCreated()
      onClose()
    } catch (err) {
      console.error('Erro ao criar ordem de compra', err)
      setError('Não foi possível criar a ordem de compra. Verifique os dados e tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Overlay onClick={onClose}>
      <Panel $width='34rem' onClick={(e) => e.stopPropagation()}>
        <PanelHeader>
          <h3>Nova Ordem de Compra</h3>
          <CloseButton onClick={onClose}>
            <X size={18} />
          </CloseButton>
        </PanelHeader>
        <PanelBody>
          <FormRow>
            <div>
              <FieldLabel>Título da OC*</FieldLabel>
              <FieldInput value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Tipo*</FieldLabel>
              <FieldSelect value={type} onChange={(e) => setType(e.target.value as any)}>
                <option value='COMPRA_PONTUAL'>Compra pontual</option>
                <option value='INDUSTRIALIZACAO'>Industrialização</option>
              </FieldSelect>
            </div>
            <div>
              <FieldLabel>Status inicial</FieldLabel>
              <FieldSelect value={status} onChange={(e) => setStatus(e.target.value as any)}>
                <option value='EM_COTACAO'>Em cotação</option>
                <option value='APROVADO'>Aprovado</option>
                <option value='PEDIDO_EMITIDO'>Pedido emitido</option>
              </FieldSelect>
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Fornecedor*</FieldLabel>
              <FieldSelect value={supplierCompanyId} onChange={(e) => setSupplierCompanyId(e.target.value)}>
                <option value=''>Selecione</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.companyName}
                  </option>
                ))}
              </FieldSelect>
            </div>
            <div>
              <FieldLabel>Prazo de entrega*</FieldLabel>
              <FieldInput type='date' value={deadline} onChange={(e) => setDeadline(e.target.value)} />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Material/item</FieldLabel>
              <FieldInput value={itemDescription} onChange={(e) => setItemDescription(e.target.value)} />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Quantidade</FieldLabel>
              <FieldInput type='number' value={itemQuantity} onChange={(e) => setItemQuantity(e.target.value)} />
            </div>
            <div>
              <FieldLabel>Unidade</FieldLabel>
              <FieldInput
                value={itemUnitOfMeasurement}
                onChange={(e) => setItemUnitOfMeasurement(e.target.value)}
                placeholder='UN, KG, M...'
              />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Valor total*</FieldLabel>
              <FieldInput type='number' value={totalValue} onChange={(e) => setTotalValue(e.target.value)} />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Detalhes/observações</FieldLabel>
              <FieldInput value={observations} onChange={(e) => setObservations(e.target.value)} />
            </div>
          </FormRow>
          {error && <ErrorText>{error}</ErrorText>}
        </PanelBody>
        <PanelFooter>
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton disabled={!isValid || saving} onClick={handleSubmit}>
            {saving ? 'Salvando...' : 'Criar ordem de compra'}
          </PrimaryButton>
        </PanelFooter>
      </Panel>
    </Overlay>
  )
}

export default CreateOrderModal
